import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

// Auto photo-card generator using HTML5 canvas
export default function PhotoCard({ storeId, website }) {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [template, setTemplate] = useState("classic");
  const canvasRef = useRef(null);

  useEffect(() => {
    if (storeId) api.get(`/products?store_id=${storeId}`).then(r => setProducts(r.data));
  }, [storeId]);

  useEffect(() => { if (selected) render(); }, [selected, template, website]);

  const render = async () => {
    const c = canvasRef.current; if (!c || !selected) return;
    const ctx = c.getContext("2d");
    c.width = 1080; c.height = 1080;
    const brand = website?.primary_color || "#0EA5E9";

    // Background
    if (template === "classic") {
      const g = ctx.createLinearGradient(0, 0, 1080, 1080);
      g.addColorStop(0, brand); g.addColorStop(1, "#0F172A");
      ctx.fillStyle = g; ctx.fillRect(0, 0, 1080, 1080);
    } else if (template === "minimal") {
      ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, 1080, 1080);
    } else {
      ctx.fillStyle = "#0F172A"; ctx.fillRect(0, 0, 1080, 1080);
    }

    // Product image
    try {
      const img = await loadImg(fileUrl(selected.image_url) || "https://placehold.co/800");
      const size = 600;
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(240, 140, size, size, 32);
      ctx.clip();
      ctx.drawImage(img, 240, 140, size, size);
      ctx.restore();
    } catch {}

    // Discount badge
    const off = selected.compare_price > selected.price ? Math.round((1 - selected.price / selected.compare_price) * 100) : 0;
    if (off > 0) {
      ctx.fillStyle = "#E11D48";
      ctx.beginPath(); ctx.arc(820, 220, 90, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#fff"; ctx.font = "900 44px 'Plus Jakarta Sans', sans-serif";
      ctx.textAlign = "center"; ctx.fillText(`${off}% OFF`, 820, 210);
      ctx.font = "600 18px sans-serif"; ctx.fillText("SAVE", 820, 240);
    }

    // Title
    ctx.textAlign = "center";
    ctx.fillStyle = template === "minimal" ? "#0F172A" : "#fff";
    ctx.font = "900 56px 'Plus Jakarta Sans', sans-serif";
    wrap(ctx, selected.name, 540, 820, 900, 64);

    // Price
    ctx.font = "900 80px 'JetBrains Mono', monospace";
    ctx.fillStyle = template === "minimal" ? brand : "#FCD34D";
    ctx.fillText(`৳ ${selected.price}`, 540, 950);

    if (selected.compare_price > selected.price) {
      ctx.font = "600 32px sans-serif";
      ctx.fillStyle = template === "minimal" ? "#94A3B8" : "rgba(255,255,255,0.6)";
      const cw = ctx.measureText(`৳${selected.compare_price}`).width;
      ctx.fillText(`৳${selected.compare_price}`, 540, 1000);
      ctx.strokeStyle = ctx.fillStyle; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(540 - cw/2, 990); ctx.lineTo(540 + cw/2, 990); ctx.stroke();
    }

    // Brand
    ctx.font = "700 24px sans-serif";
    ctx.fillStyle = template === "minimal" ? "#64748B" : "rgba(255,255,255,0.7)";
    ctx.fillText(website?.name || "OxyCom Store", 540, 1050);
  };

  const wrap = (ctx, text, x, y, maxW, lh) => {
    const words = text.split(" "); let line = ""; let yy = y;
    for (let w of words) {
      const test = line + w + " ";
      if (ctx.measureText(test).width > maxW) { ctx.fillText(line, x, yy); line = w + " "; yy += lh; }
      else line = test;
    }
    ctx.fillText(line, x, yy);
  };

  const loadImg = (src) => new Promise((res, rej) => {
    const img = new Image(); img.crossOrigin = "anonymous";
    img.onload = () => res(img); img.onerror = rej; img.src = src;
  });

  const download = () => {
    const c = canvasRef.current;
    const link = document.createElement("a");
    link.download = `${selected.name}-photocard.png`;
    link.href = c.toDataURL("image/png");
    link.click();
    toast.success("Downloaded");
  };

  if (!products.length) return <div className="text-center py-16 text-slate-400">Add products first</div>;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <h3 className="font-bold mb-3">Select product</h3>
        <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 bg-white rounded-lg border">
          {products.map(p => (
            <button key={p.product_id} onClick={() => setSelected(p)} className={`text-left p-2 rounded-lg border-2 ${selected?.product_id === p.product_id ? "border-sky-500" : "border-transparent hover:border-slate-200"}`} data-testid={`select-prod-${p.product_id}`}>
              <img src={fileUrl(p.image_url) || "https://placehold.co/80"} className="w-full aspect-square rounded object-cover" alt=""/>
              <div className="text-xs truncate mt-1 font-semibold">{p.name}</div>
              <div className="text-xs font-mono text-sky-600">৳{p.price}</div>
            </button>
          ))}
        </div>

        <h3 className="font-bold mt-6 mb-3">Template</h3>
        <div className="flex gap-2">
          {["classic", "minimal", "dark"].map(t => (
            <button key={t} onClick={() => setTemplate(t)} className={`px-4 py-2 rounded-full border-2 text-sm font-semibold ${template === t ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white"}`} data-testid={`template-${t}`}>
              {t}
            </button>
          ))}
        </div>

        {selected && (
          <div className="mt-6 flex gap-2">
            <Button onClick={render} variant="outline"><RefreshCw className="w-4 h-4 mr-1.5"/>Regenerate</Button>
            <Button onClick={download} className="bg-sky-500 hover:bg-sky-600" data-testid="download-card"><Download className="w-4 h-4 mr-1.5"/>Download PNG</Button>
          </div>
        )}
      </div>

      <div>
        <h3 className="font-bold mb-3">Preview</h3>
        <div className="bg-white rounded-2xl border border-slate-200 p-3">
          <canvas ref={canvasRef} className="w-full h-auto rounded-lg" style={{ aspectRatio: "1/1" }}/>
          {!selected && <div className="text-center text-slate-400 py-8 absolute">Select a product</div>}
        </div>
      </div>
    </div>
  );
}
